import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { userAccountIdSelector } from 'selectors/user/user-features-selector';

import { resolveActivePins } from '../launcher-config';
import { buildNextHeaderPins, getDefaultHeaderPinPaths, persistHeaderPins } from '../header-pins';
import type { HeaderPinLink, HeaderPinsRole, HeaderPinsUserId } from '../header-pins';
import { useStoredHeaderPins } from './use-stored-header-pins';

interface UseHeaderPinsParams {
    visibleLinks: HeaderPinLink[];
    role: HeaderPinsRole;
}

export const useHeaderPins = ({ visibleLinks, role }: UseHeaderPinsParams) => {
    const userId: HeaderPinsUserId = useSelector(userAccountIdSelector);
    const storedPins = useStoredHeaderPins();

    const defaultPins = useMemo(() => getDefaultHeaderPinPaths(visibleLinks, role), [visibleLinks, role]);

    // Empty array must fall back to defaults (?? alone treats [] as valid).
    const activePins = useMemo(
        () =>
            resolveActivePins(
                storedPins,
                defaultPins,
                visibleLinks.map(link => link.to)
            ),
        [storedPins, defaultPins, visibleLinks]
    );

    const toggleHeaderPin = useCallback(
        (path: string, checked: boolean) => {
            const next = buildNextHeaderPins({
                storedPins,
                defaultPins,
                visibleLinks,
                path,
                checked
            });
            persistHeaderPins(next, userId);
        },
        [storedPins, defaultPins, visibleLinks, userId]
    );

    return { storedPins, defaultPins, activePins, toggleHeaderPin };
};
